import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getCsrfToken } from "../../../../public/api/authApi";
import GlobalFeedback from "../../../../global/GlobalFeedback";
import ScheduleForm from "./ScheduleForm";
import { getScheduleById, updateSchedule } from "../../api/schedulesApi";
import { scheduleToFormValues } from "../../utils/scheduleValidation";
import { ensureCsrfToken, safeErrorText } from "../../utils/scheduleHelpers";

export default function ScheduleEditPanel({
  scheduleId,
  schedule: scheduleProp = null,
  onCancel,
  onSuccess,
}) {
  const [schedule, setSchedule] = useState(scheduleProp);
  const [loading, setLoading] = useState(!scheduleProp);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const load = useCallback(async () => {
    if (!scheduleId) return;
    setLoading(true);
    setError("");
    try {
      const res = await getScheduleById(scheduleId);
      setSchedule(res?.data || null);
    } catch (err) {
      setError(safeErrorText(err, "Failed to load schedule"));
      setSchedule(null);
    } finally {
      setLoading(false);
    }
  }, [scheduleId]);

  useEffect(() => {
    if (scheduleProp) {
      setSchedule(scheduleProp);
      setLoading(false);
      return;
    }
    load();
  }, [scheduleProp, load]);

  const initialValues = useMemo(
    () => (schedule ? scheduleToFormValues(schedule) : undefined),
    [schedule],
  );

  const handleSubmit = async (payload) => {
    if (!schedule?.id) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const csrfToken = await ensureCsrfToken(getCsrfToken);
      const res = await updateSchedule(schedule.id, payload, csrfToken);
      onSuccess?.(res?.message || "Schedule updated successfully");
    } catch (err) {
      setSubmitError(safeErrorText(err, "Failed to update schedule"));
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <p className="schedule-table-loading">Loading schedule…</p>;
  }

  if (!schedule) {
    return <GlobalFeedback type="error" text={error || "Schedule not found"} role="alert" />;
  }

  return (
    <div>
      <GlobalFeedback type="error" text={error} role="alert" />
      <ScheduleForm
        mode="edit"
        initialValues={initialValues}
        assignedToName={schedule.assigned_to_name}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        submitting={submitting}
        serverError={submitError}
      />
    </div>
  );
}
